import { VisuallyHidden } from "@mantine/core";
import { useEffect, useRef } from "react";

import logoSvg from "./assets/created-ai-logo.svg?raw";
import classes from "./GenerationLoader.module.css";

// Live's loader on a generation in progress (colortv-web `components/generation-loader`, read for
// its behaviour, spec ui-perf/ui-switch-classic-simple-pro decision 18): dots in the accent
// gradient gather into the Created.ai mark, hold, scatter over the tile and gather again, every
// 4.2 s. The mark is our own logo's (`assets/created-ai-logo.svg`, its first path).

/** The mark's outline, in the logo's units, made once on the first draw. */
const MARK_PATH = /<path d="([^"]+)"/.exec(logoSvg)?.[1] ?? "";
let mark: Path2D | undefined;
const MARK_WIDTH = 28.11;
const MARK_HEIGHT = 19;

/** The mark at 55% of the tile's shorter side, never under 50 px. */
const MARK_SHARE = 0.55;
const MARK_MIN_PX = 50;
const DOT_RADIUS = 0.9;
const DOT_SPACING_PX = 3.25;
/** How far past the tile a scattered dot may sit. */
const BLEED_PX = 16;
const CYCLE_MS = 4200;
/** The cycle's parts: gather until 30%, hold until 60%, scatter until 90%, then rest scattered. */
const GATHERED = 0.3;
const HELD = 0.6;
const SCATTERED = 0.9;
const SCATTERED_ALPHA = 0.25;

interface Dot {
  x: number;
  y: number;
  fromX: number;
  fromY: number;
}

const ease = (t: number) => t * t * (3 - 2 * t);

/** How far gathered the dots are (0 to 1), and how opaque, at a point of the cycle (0 to 1). */
export function loaderPhase(at: number): { gathered: number; alpha: number } {
  if (at < GATHERED) {
    const t = at / GATHERED;
    return { gathered: ease(t), alpha: SCATTERED_ALPHA + (1 - SCATTERED_ALPHA) * t };
  }
  if (at < HELD) return { gathered: 1, alpha: 1 };
  if (at < SCATTERED) {
    const t = (at - HELD) / (SCATTERED - HELD);
    return { gathered: 1 - ease(t), alpha: 1 - (1 - SCATTERED_ALPHA) * t };
  }
  return { gathered: 0, alpha: SCATTERED_ALPHA };
}

/** The dots that fill the mark at its place in a tile of this size, each with a scattered start. */
function dotsFor(ctx: CanvasRenderingContext2D, width: number, height: number): Dot[] {
  const markWidth = Math.max(MARK_MIN_PX, Math.min(width, height) * MARK_SHARE);
  const scale = markWidth / MARK_WIDTH;
  const left = (width - MARK_WIDTH * scale) / 2;
  const top = (height - MARK_HEIGHT * scale) / 2;
  const step = DOT_SPACING_PX / scale;
  const dots: Dot[] = [];
  mark ??= new Path2D(MARK_PATH);
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  for (let y = 0; y <= MARK_HEIGHT; y += step) {
    for (let x = 0; x <= MARK_WIDTH; x += step) {
      if (!ctx.isPointInPath(mark, x, y)) continue;
      dots.push({
        x: left + x * scale,
        y: top + y * scale,
        fromX: -BLEED_PX + Math.random() * (width + 2 * BLEED_PX),
        fromY: -BLEED_PX + Math.random() * (height + 2 * BLEED_PX),
      });
    }
  }
  ctx.restore();
  return dots;
}

/**
 * A generation in progress, as live shows one: its own canvas over the tile it fills, still where
 * the person asks for less motion, and stopped while off screen. Draws nothing where there is no
 * 2D canvas (jsdom).
 */
export interface GenerationLoaderProps {
  /** Read by a screen reader: "Output 2, waiting". */
  label: string;
  /**
   * A video being made: under the dots, live's blurred placeholder with its slow blur pulse, as
   * live's running video card shows it (`blured-media-placeholder.jpg`, spec
   * ui-perf/ui-switch-classic-simple-pro, decision 20).
   */
  video?: boolean;
}

export function GenerationLoader({ label, video = false }: GenerationLoaderProps) {
  const box = useRef<HTMLOutputElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const root = box.current;
    const surface = canvas.current;
    // No 2D canvas here (jsdom): the status stays, with nothing drawn.
    if (typeof Path2D === "undefined") return;
    const ctx = surface?.getContext("2d") ?? null;
    if (root === null || surface === null || ctx === null) return;
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
    const css = getComputedStyle(root);
    let dots: Dot[] = [];
    let fill: CanvasGradient | string = "transparent";
    let size = { width: 0, height: 0 };
    let frame: number | null = null;
    const draw = (gathered: number, alpha: number) => {
      ctx.clearRect(0, 0, size.width, size.height);
      ctx.fillStyle = fill;
      ctx.globalAlpha = alpha;
      // Every dot in one path and one fill a frame: hundreds of fills a frame cost far more.
      ctx.beginPath();
      for (const dot of dots) {
        const x = dot.fromX + (dot.x - dot.fromX) * gathered;
        const y = dot.fromY + (dot.y - dot.fromY) * gathered;
        // A new sub-path at each dot's edge, so no line joins one dot to the next.
        ctx.moveTo(x + DOT_RADIUS, y);
        ctx.arc(x, y, DOT_RADIUS, 0, Math.PI * 2);
      }
      ctx.fill();
    };
    const tick = (now: number) => {
      const { gathered, alpha } = loaderPhase((now % CYCLE_MS) / CYCLE_MS);
      draw(gathered, alpha);
      frame = requestAnimationFrame(tick);
    };
    const start = () => {
      if (frame === null && !still) frame = requestAnimationFrame(tick);
    };
    const stop = () => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
    };
    const layout = (width: number, height: number) => {
      const ratio = window.devicePixelRatio || 1;
      surface.width = Math.max(1, Math.round(width * ratio));
      surface.height = Math.max(1, Math.round(height * ratio));
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      size = { width, height };
      dots = dotsFor(ctx, width, height);
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, css.getPropertyValue("--cai-loader-from").trim());
      gradient.addColorStop(0.5, css.getPropertyValue("--cai-loader-mid").trim());
      gradient.addColorStop(1, css.getPropertyValue("--cai-loader-to").trim());
      fill = gradient;
      if (still) draw(1, 1);
    };
    // A new size only past 2 px, so a tile that settles does not scatter its dots anew.
    const resize = new ResizeObserver(([entry]) => {
      const width = Math.round(entry?.contentRect.width ?? 0);
      const height = Math.round(entry?.contentRect.height ?? 0);
      if (width <= 0 || height <= 0) return;
      if (Math.abs(width - size.width) <= 2 && Math.abs(height - size.height) <= 2) return;
      layout(width, height);
    });
    const seen = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting === true) start();
      else stop();
    });
    resize.observe(root);
    seen.observe(root);
    return () => {
      resize.disconnect();
      seen.disconnect();
      stop();
    };
  }, []);
  return (
    // The words are the status's own, read as a placeholder's are (`MediaPlaceholder`).
    <output
      ref={box}
      className={video ? `${classes.root} ${classes.video}` : classes.root}
      data-medium={video ? "video" : undefined}
    >
      <canvas ref={canvas} className={classes.canvas} aria-hidden />
      <VisuallyHidden>{label}</VisuallyHidden>
    </output>
  );
}
